import { IconType } from 'react-icons'
import { TbKey, TbLayoutDashboardFilled, TbPhone, TbUsers, TbRoute, TbTool } from 'react-icons/tb'

import { hasAnyPermission } from '@/utils/permissions'
import { AccessCategory, UserIntrospectionResponse } from '@/api/main/types.gen'

export type NavigationTab = {
  label: string
  icon: IconType
  link?: string
  permission: AccessCategory | null
  initiallyOpened?: boolean
  links?: {
    label: string
    link: string
    permission?: AccessCategory | null
  }[]
}

export type NavigationTabs = {
  system: NavigationTab[]
  users: NavigationTab[]
}

export const navigationTabs: NavigationTabs = {
  system: [
    {
      label: 'home',
      icon: TbLayoutDashboardFilled,
      link: '/',
      permission: null,
    },
    {
      label: 'dispatching',
      icon: TbPhone,
      initiallyOpened: true,
      permission: null,
      links: [
        { link: '/hotline-calls', label: 'hotlineCalls', permission: 'hotline' },
        {
          link: '/vehicles',
          label: 'vehicles',
          permission: 'vehicles',
        },
        {
          link: '/unexpected-events',
          label: 'unexpectedEvents',
          permission: 'unexpected_events',
        },
      ],
    },
    {
      label: 'people',
      icon: TbUsers,
      initiallyOpened: true,
      permission: null,
      links: [
        { link: '/shifts', label: 'shifts', permission: 'shifts' },
        {
          link: '/drivers',
          label: 'drivers',
          permission: 'drivers',
        },
      ],
    },
    {
      label: 'driving',
      icon: TbRoute,
      initiallyOpened: true,
      permission: null,
      links: [
        { link: '/routes', label: 'routes', permission: 'routes' },
        { link: '/drive-statistics', label: 'driveStatistics', permission: 'drive_statistics' },
        { link: '/driver-statistics', label: 'driverStatistics', permission: 'drive_statistics' },
        { link: '/route-statistics', label: 'routeStatistics', permission: 'drive_statistics' },
        { link: '/scheduling', label: 'scheduling', permission: 'scheduling' },
      ],
    },
    {
      label: 'tools',
      icon: TbTool,
      initiallyOpened: true,
      permission: null,
      links: [{ link: '/control-data', label: 'controlDataEdit', permission: null }],
    } as NavigationTab,
  ],
  users: [
    {
      link: '/change-password',
      label: 'resetPassword',
      icon: TbKey,
      permission: 'users',
    },
  ],
}

/**
 * Filter a single navigation tab by the user's permissions.
 * Groups (tabs with `links`) keep only the links the user can access, and are
 * dropped entirely when no link remains. Leaf tabs are kept only if the user
 * has the required permission (or none is required).
 */
export const filterNavigationTab = (
  item: NavigationTab,
  userPermissions: UserIntrospectionResponse | null
): NavigationTab | null => {
  const filteredLinks = item.links
    ? item.links.filter(
        link => !link.permission || hasAnyPermission(userPermissions, link.permission as AccessCategory)
      )
    : undefined

  if (item.links) {
    if (filteredLinks && filteredLinks.length > 0) {
      return { ...item, links: filteredLinks }
    }
    return null
  } else {
    if (!item.permission || hasAnyPermission(userPermissions, item.permission as AccessCategory)) {
      return item
    }
    return null
  }
}

/**
 * Apply {@link filterNavigationTab} to every section, returning the same
 * `{ system, users }` shape with only the entries the user can access.
 */
export const filterNavigationTabs = (
  tabs: NavigationTabs,
  userPermissions: UserIntrospectionResponse | null
): NavigationTabs => ({
  system: tabs.system
    .map(item => filterNavigationTab(item, userPermissions))
    .filter((item): item is NavigationTab => item !== null),
  users: tabs.users
    .map(item => filterNavigationTab(item, userPermissions))
    .filter((item): item is NavigationTab => item !== null),
})

export type SearchableLink = {
  /** Route path, e.g. '/vehicles'. */
  link: string
  /** i18n key in the 'nav' namespace — UNTRANSLATED so it re-translates on language switch. */
  labelKey: string
  /** Parent tab label key for grouping, e.g. 'dispatching'. Undefined for top-level leaves. */
  groupKey?: string
  /** Icon of the link's tab (the parent tab for group children). */
  icon: IconType
  section: 'system' | 'users'
}

/**
 * Flatten (already permission-filtered) navigation tabs into a list of
 * leaf links suitable for the command palette. Leaf tabs become one entry;
 * group tabs contribute one entry per child link carrying the parent label key.
 */
export const flattenSearchableLinks = (tabs: NavigationTabs): SearchableLink[] => {
  const result: SearchableLink[] = []

  const collect = (section: 'system' | 'users') => {
    for (const tab of tabs[section]) {
      if (tab.links && tab.links.length > 0) {
        for (const child of tab.links) {
          result.push({ link: child.link, labelKey: child.label, groupKey: tab.label, icon: tab.icon, section })
        }
      } else if (tab.link) {
        result.push({ link: tab.link, labelKey: tab.label, icon: tab.icon, section })
      }
    }
  }

  collect('system')
  collect('users')

  return result
}

/**
 * Resolve a (possibly parameterized) pathname to the canonical searchable link
 * it belongs to, using the same longest-prefix match the sidebar uses for
 * active-state highlighting. Returns null when no nav link matches.
 */
export const canonicalizePath = (pathname: string, searchableLinks: SearchableLink[]): string | null => {
  let best: string | null = null
  for (const { link } of searchableLinks) {
    const matches = pathname === link || pathname.startsWith(`${link}/`)
    if (matches && (best === null || link.length > best.length)) {
      best = link
    }
  }
  return best
}
